import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import {
  Wallet,
  Ticket as TicketIcon,
  Activity,
  Inbox,
  Upload,
  ShieldCheck,
  ShieldAlert,
  ShieldX,
  MailWarning,
  ArrowDownLeft,
  ArrowUpRight,
  RefreshCcw,
} from 'lucide-react';
import api from '../lib/api';
import { useAuth } from '../lib/auth';
import { formatZAR, formatApiError } from '../lib/format';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Badge } from '../components/ui/badge';

const kycBadge = (status) => {
  switch (status) {
    case 'verified':
      return <span data-testid="kyc-status-verified" className="inline-flex items-center gap-1 text-[#D4AF37]"><ShieldCheck size={16} /> Verified</span>;
    case 'pending':
      return <span data-testid="kyc-status-pending" className="inline-flex items-center gap-1 text-[#FFD700]"><ShieldAlert size={16} /> Pending</span>;
    case 'rejected':
      return <span data-testid="kyc-status-rejected" className="inline-flex items-center gap-1 text-[#F87171]"><ShieldX size={16} /> Rejected</span>;
    default:
      return <span data-testid="kyc-status-none" className="text-[#C0C0C0]">Not submitted</span>;
  }
};

export default function Dashboard() {
  const { user, refresh } = useAuth();
  const [tickets, setTickets] = useState([]);
  const [stats, setStats] = useState({ total: 0, active_this_week: 0 });
  const [messages, setMessages] = useState([]);
  const [unread, setUnread] = useState(0);
  const [walletHistory, setWalletHistory] = useState([]);

  const [idNumber, setIdNumber] = useState('');
  const [idDoc, setIdDoc] = useState(null);
  const [selfie, setSelfie] = useState(null);
  const [kycLoading, setKycLoading] = useState(false);

  const loadAll = async () => {
    try {
      const [t, m, w] = await Promise.all([
        api.get('/tickets/my'),
        api.get('/messages/my'),
        api.get('/wallet/history'),
      ]);
      setTickets(t.data?.tickets || []);
      setStats({ total: t.data?.total || 0, active_this_week: t.data?.active_this_week || 0 });
      setMessages(m.data?.messages || []);
      setUnread(m.data?.unread || 0);
      setWalletHistory(w.data?.history || []);
    } catch {}
  };

  useEffect(() => {
    loadAll();
  }, []);

  const submitKyc = async (e) => {
    e.preventDefault();
    if (!idNumber || !idDoc || !selfie) {
      toast.error('Please fill in ID number and upload both files');
      return;
    }
    setKycLoading(true);
    try {
      const fd = new FormData();
      fd.append('id_number', idNumber);
      fd.append('id_doc', idDoc);
      fd.append('selfie', selfie);
      await api.post('/kyc/submit', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('KYC submitted — pending review');
      setIdNumber('');
      setIdDoc(null);
      setSelfie(null);
      await refresh();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    } finally {
      setKycLoading(false);
    }
  };

  const markRead = async () => {
    try {
      await api.post('/messages/mark-read');
      setUnread(0);
      setMessages((m) => m.map((x) => ({ ...x, read: true })));
    } catch {}
  };

  if (!user) return null;

  return (
    <div data-testid="dashboard-page" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <div className="flex items-end justify-between flex-wrap gap-3 mb-2">
        <div>
          <p className="text-xs uppercase tracking-[0.32em] text-[#D4AF37] font-semibold mb-2">Welcome back</p>
          <h1 className="font-serif-display text-4xl sm:text-5xl font-black">
            <span className="text-gold-gradient">
              {user.first_name} {user.surname}
            </span>
          </h1>
        </div>
        <Link to="/buy-ticket" data-testid="dashboard-buy-cta" className="btn-gold">Buy tickets</Link>
      </div>

      {!user.email_verified && (
        <div data-testid="dash-verify-warning" className="mt-6 card-embossed p-4 border-l-4 border-l-[#D4AF37] flex items-start gap-3">
          <MailWarning className="text-[#D4AF37] mt-0.5" size={20} />
          <div>
            <p className="font-semibold text-white">Verify your email</p>
            <p className="text-sm text-[#C0C0C0]">
              Ticket purchases are disabled until you verify. Check <Link to={`/check-email?email=${encodeURIComponent(user.email)}`} className="text-[#D4AF37]">your email</Link>.
            </p>
          </div>
        </div>
      )}

      <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-5">
        <div className="card-embossed p-6">
          <div className="flex items-center gap-2 text-[#C0C0C0]"><Wallet size={18} /> <span className="text-xs uppercase tracking-[0.2em]">Wallet</span></div>
          <p data-testid="wallet-balance" className="mt-3 font-serif-display text-4xl text-gold-gradient font-black">
            {formatZAR(user.wallet_balance || 0)}
          </p>
        </div>
        <div className="card-embossed p-6">
          <div className="flex items-center gap-2 text-[#C0C0C0]"><TicketIcon size={18} /> <span className="text-xs uppercase tracking-[0.2em]">Total tickets</span></div>
          <p data-testid="stat-total-tickets" className="mt-3 font-serif-display text-4xl text-white font-black">{stats.total}</p>
        </div>
        <div className="card-embossed p-6">
          <div className="flex items-center gap-2 text-[#C0C0C0]"><Activity size={18} /> <span className="text-xs uppercase tracking-[0.2em]">This week's active</span></div>
          <p data-testid="stat-active-week" className="mt-3 font-serif-display text-4xl text-gold-gradient font-black">{stats.active_this_week}</p>
        </div>
      </div>

      {/* Tickets */}
      <div className="mt-10 card-embossed p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-serif-display text-xl text-white font-bold">Ticket history</h3>
          <span className="text-xs text-[#C0C0C0]/70">{tickets.length} total</span>
        </div>
        {tickets.length === 0 ? (
          <p data-testid="tickets-empty" className="text-sm text-[#C0C0C0]">No tickets yet. Grab your first Golden Catch →</p>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="border-[#D4AF37]/15">
                  <TableHead className="text-[#C0C0C0]">Purchased</TableHead>
                  <TableHead className="text-[#C0C0C0]">Ticket</TableHead>
                  <TableHead className="text-[#C0C0C0]">Qty</TableHead>
                  <TableHead className="text-[#C0C0C0]">Amount</TableHead>
                  <TableHead className="text-[#C0C0C0]">Draw</TableHead>
                  <TableHead className="text-[#C0C0C0]">Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {tickets.map((t) => (
                  <TableRow key={t.id} className="border-[#D4AF37]/10" data-testid={`ticket-row-${t.id}`}>
                    <TableCell className="text-white/90 text-sm">{new Date(t.created_at).toLocaleString()}</TableCell>
                    <TableCell className="text-[#D4AF37] text-sm font-mono">{t.ticket_number}</TableCell>
                    <TableCell className="text-white text-sm">{t.qty}</TableCell>
                    <TableCell className="text-white text-sm">{formatZAR(t.amount)}</TableCell>
                    <TableCell className="text-white/90 text-sm">{new Date(t.draw_date).toDateString()}</TableCell>
                    <TableCell>
                      <Badge className="bg-[#D4AF37]/15 text-[#D4AF37] hover:bg-[#D4AF37]/20 capitalize">{t.status}</Badge>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>

      {/* Wallet history */}
      <div className="mt-10 card-embossed p-6" data-testid="wallet-history-section">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Wallet size={18} className="text-[#D4AF37]" />
            <h3 className="font-serif-display text-xl text-white font-bold">Wallet history</h3>
          </div>
          <span className="text-xs text-[#C0C0C0]/70">{walletHistory.length} entries</span>
        </div>
        {walletHistory.length === 0 ? (
          <p data-testid="wallet-empty" className="text-sm text-[#C0C0C0]">No wallet activity yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="border-[#D4AF37]/15">
                  <TableHead className="text-[#C0C0C0]">Date</TableHead>
                  <TableHead className="text-[#C0C0C0]">Type</TableHead>
                  <TableHead className="text-[#C0C0C0]">Amount</TableHead>
                  <TableHead className="text-[#C0C0C0]">Description</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {walletHistory.map((w) => {
                  const debit = Number(w.amount) < 0;
                  const Icon = w.type === 'refund' ? RefreshCcw : debit ? ArrowUpRight : ArrowDownLeft;
                  return (
                    <TableRow
                      key={w.id}
                      data-testid={`wallet-row-${w.id}`}
                      className="border-[#D4AF37]/10"
                    >
                      <TableCell className="text-white/90 text-sm">
                        {new Date(w.created_at).toLocaleString()}
                      </TableCell>
                      <TableCell>
                        <span className="inline-flex items-center gap-1.5 text-xs capitalize text-[#C0C0C0]">
                          <Icon size={14} className={debit ? 'text-[#F87171]' : 'text-[#4ADE80]'} />
                          {w.type}
                        </span>
                      </TableCell>
                      <TableCell>
                        <span
                          data-testid={`wallet-amount-${w.id}`}
                          className={`text-sm font-semibold ${debit ? 'text-[#F87171]' : 'text-[#4ADE80]'}`}
                        >
                          {debit ? '-' : '+'}{formatZAR(Math.abs(w.amount))}
                        </span>
                      </TableCell>
                      <TableCell className="text-[#C0C0C0] text-sm">{w.description}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        )}
      </div>

      {/* KYC + Inbox */}
      <div className="mt-10 grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="card-embossed p-6">
          <div className="flex items-center justify-between">
            <h3 className="font-serif-display text-xl text-white font-bold">KYC Verification</h3>
            {kycBadge(user.kyc?.status)}
          </div>
          <p className="text-sm text-[#C0C0C0]/80 mt-1">Required to receive prize payouts.</p>

          {user.kyc?.status === 'verified' ? (
            <div className="mt-6 rounded-lg bg-[#0a0a0a] border border-[#D4AF37]/20 p-4">
              <p className="text-sm text-[#C0C0C0]">Your identity has been verified. You're ready to receive payouts.</p>
            </div>
          ) : (
            <form onSubmit={submitKyc} className="mt-6 space-y-4" data-testid="kyc-form">
              <div>
                <label className="text-xs uppercase tracking-[0.2em] text-[#C0C0C0]/80">SA ID Number</label>
                <input
                  data-testid="kyc-idnumber"
                  type="text"
                  required
                  value={idNumber}
                  onChange={(e) => setIdNumber(e.target.value)}
                  className="mt-2 w-full bg-[#0a0a0a] border border-[#D4AF37]/30 rounded-lg px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-[#D4AF37]"
                  placeholder="e.g. 9001015800080"
                />
              </div>
              <div>
                <label className="text-xs uppercase tracking-[0.2em] text-[#C0C0C0]/80">ID Document</label>
                <label className="mt-2 flex items-center gap-3 border border-dashed border-[#D4AF37]/40 rounded-lg px-4 py-3 cursor-pointer hover:bg-[#D4AF37]/5">
                  <Upload size={18} className="text-[#D4AF37]" />
                  <span className="text-sm text-[#C0C0C0] truncate">
                    {idDoc ? idDoc.name : 'Upload ID doc (jpg, png, pdf)'}
                  </span>
                  <input
                    data-testid="kyc-iddoc"
                    type="file"
                    accept=".jpg,.jpeg,.png,.pdf,.webp"
                    className="hidden"
                    onChange={(e) => setIdDoc(e.target.files?.[0] || null)}
                  />
                </label>
              </div>
              <div>
                <label className="text-xs uppercase tracking-[0.2em] text-[#C0C0C0]/80">Selfie</label>
                <label className="mt-2 flex items-center gap-3 border border-dashed border-[#D4AF37]/40 rounded-lg px-4 py-3 cursor-pointer hover:bg-[#D4AF37]/5">
                  <Upload size={18} className="text-[#D4AF37]" />
                  <span className="text-sm text-[#C0C0C0] truncate">
                    {selfie ? selfie.name : 'Upload selfie (jpg, png)'}
                  </span>
                  <input
                    data-testid="kyc-selfie"
                    type="file"
                    accept=".jpg,.jpeg,.png,.webp"
                    className="hidden"
                    onChange={(e) => setSelfie(e.target.files?.[0] || null)}
                  />
                </label>
              </div>
              <button
                type="submit"
                data-testid="kyc-submit"
                disabled={kycLoading || user.kyc?.status === 'pending'}
                className="btn-gold w-full"
              >
                {kycLoading ? 'Submitting…' : user.kyc?.status === 'pending' ? 'Awaiting review' : 'Submit for review'}
              </button>
            </form>
          )}
        </div>

        <div className="card-embossed p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Inbox size={18} className="text-[#D4AF37]" />
              <h3 className="font-serif-display text-xl text-white font-bold">Inbox</h3>
              {unread > 0 && (
                <span data-testid="inbox-unread" className="ml-1 text-xs bg-[#D4AF37] text-black rounded-full px-2 py-0.5 font-bold">
                  {unread}
                </span>
              )}
            </div>
            {unread > 0 && (
              <button data-testid="inbox-mark-read" onClick={markRead} className="text-xs text-[#C0C0C0] hover:text-[#D4AF37]">
                Mark all read
              </button>
            )}
          </div>

          <div className="mt-4 space-y-3 max-h-[420px] overflow-y-auto">
            {messages.length === 0 && (
              <p data-testid="inbox-empty" className="text-sm text-[#C0C0C0]/80">No messages yet.</p>
            )}
            {messages.map((m) => (
              <div
                key={m.id}
                data-testid={`inbox-msg-${m.id}`}
                className={`rounded-lg border p-4 ${m.read ? 'border-[#D4AF37]/10 bg-[#0a0a0a]' : 'border-[#D4AF37]/40 bg-[#141010]'}`}
              >
                <div className="flex items-center justify-between">
                  <p className="text-sm font-semibold text-white">{m.subject}</p>
                  <span className="text-[10px] uppercase tracking-widest text-[#C0C0C0]/70">
                    {new Date(m.created_at).toLocaleDateString()}
                  </span>
                </div>
                <p className="mt-2 text-sm text-[#C0C0C0] whitespace-pre-wrap">{m.body}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
